import {
  Add as AddIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowBack as BackIcon,
  ViewModule as CapsuleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Quiz as QuizIcon,
  CheckCircle as SavedIcon,
  Event as WorkshopIcon,
  Group as GroupIcon,
  FactCheck as ValidateIcon,
  Translate as TranslateIcon,
  ReportProblem as RequiredIcon
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CapsuleEditorDialog, { ICapsule } from '../components/CapsuleEditorDialog';
import ImageUpload from '../components/ImageUpload';
import MarkdownEditorMultilingual from '../components/MarkdownEditorMultilingual';
import MultilingualInput from '../components/MultilingualInput';
import QuizEditor, { IQuiz } from '../components/QuizEditor';
import WorkshopEditor, { IWorkshop } from '../components/WorkshopEditor';
import AssessmentEditor from '../components/AssessmentEditor';
import { validateCourseCompleteness, ValidationResult } from '../utils/courseValidation';
import { ensureQuizQuestionIds, serializeChaptersQuizItemsForBackend } from '../utils/quizIds';
import {
  CourseAssessment,
  CourseAssessmentResponseEnvelope,
  buildEmptyAssessment,
  validateAssessment,
  pickLocalizedTranslation
} from '../types/assessment';
import {
  fetchCourseAssessment,
  setCourseAssessmentEnabled,
  updateCourseAssessment,
  fetchAssessmentResponses
} from '../services/assessmentApi';
import { useAuth } from '../contexts/AuthContext';

type ChapterItem = (ICapsule & { itemType: 'capsule' }) | (IQuiz & { itemType: 'quiz' }) | (IWorkshop & { itemType: 'workshop' });

interface Chapter {
  id: string;
  _id?: string;
  title: { [key: string]: string };
  description?: { [key: string]: string };
  summary?: { [key: string]: string };
  order: number;
  icon?: 'flag' | 'people' | 'award' | 'book' | 'film' | 'edit';
  requiresRating?: boolean;
  items: ChapterItem[];
}

interface ChapterOverride {
  chapter: string;
  deadline?: string;
  unlockDate?: string;
}

interface WorkshopOverride {
  chapter: string;
  itemId: string;
  title?: { [key: string]: string };
  description?: { [key: string]: string };
  image?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  details?: { [key: string]: string };
  lieu?: { [key: string]: string };
  distanciel?: boolean;
}

interface Group {
  id: string;
  name: string;
  trainer?: string | null;
  studentCount?: number;
  /** ISO — accès formation bloqué avant cette date (optionnel, par groupe) */
  courseUnlockDate?: string | null;
  /** ISO — dates affichées sur le certificat (formations blended) */
  certificateStartDate?: string | null;
  certificateEndDate?: string | null;
  chapterOverrides?: ChapterOverride[];
  workshopOverrides?: WorkshopOverride[];
  /** Dates de déblocage des mesures d'impact de suivi pour ce groupe */
  followupImpactDates?: Array<{ followupId: string; unlockDate: string }>;
  /** Mot de bienvenue (HTML multilingue, par groupe) */
  welcomeMessage?: { [key: string]: string } | null;
}

interface Course {
  id?: string;
  courseProject: string;
  title: { [key: string]: string };
  description: { [key: string]: string };
  image?: string;
  defaultLanguage: string;
  availableLanguages: string[];
  isActive: boolean;
  coachId: string;
  chapters: Chapter[];
  /** Tags métier (liste fixe, multi-sélection) */
  tags: string[];
  /** Type de formation — conditionne le certificat de réalisation */
  courseType?: 'elearning' | 'blended' | 'coaching' | null;
}

const COURSE_TYPE_OPTIONS: Array<{ value: 'elearning' | 'blended' | 'coaching'; label: string }> = [
  { value: 'elearning', label: 'E-learning' },
  { value: 'blended', label: 'Blended' },
  { value: 'coaching', label: 'Coaching' },
];

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' }
];

const COURSE_TAG_OPTIONS = [
  'ELearning',
  'Blended',
  'Coaching',
  'Template',
  'Autre'
] as const;

const FeatherIconBase: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const FeatherFileTextIcon: React.FC = () => (
  <FeatherIconBase>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </FeatherIconBase>
);

const FeatherFlagIcon: React.FC = () => (
  <FeatherIconBase>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </FeatherIconBase>
);

const FeatherUsersIcon: React.FC = () => (
  <FeatherIconBase>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </FeatherIconBase>
);

const FeatherAwardIcon: React.FC = () => (
  <FeatherIconBase>
    <circle cx="12" cy="8" r="7" />
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
  </FeatherIconBase>
);

const FeatherBookIcon: React.FC = () => (
  <FeatherIconBase>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </FeatherIconBase>
);

const FeatherFilmIcon: React.FC = () => (
  <FeatherIconBase>
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <line x1="7" y1="2" x2="7" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" />
    <line x1="17" y1="17" x2="22" y2="17" />
    <line x1="17" y1="7" x2="22" y2="7" />
  </FeatherIconBase>
);

const FeatherEditIcon: React.FC = () => (
  <FeatherIconBase>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </FeatherIconBase>
);

const CHAPTER_ICON_OPTIONS: Array<{
  value: '' | 'flag' | 'people' | 'award' | 'book' | 'film' | 'edit';
  label: string;
  icon: React.ReactNode;
}> = [
  { value: '', label: 'Aucune (défaut)', icon: <FeatherFileTextIcon /> },
  { value: 'flag', label: 'Drapeau', icon: <FeatherFlagIcon /> },
  { value: 'people', label: 'Groupe', icon: <FeatherUsersIcon /> },
  { value: 'award', label: 'Récompense', icon: <FeatherAwardIcon /> },
  { value: 'book', label: 'Livre', icon: <FeatherBookIcon /> },
  { value: 'film', label: 'Film', icon: <FeatherFilmIcon /> },
  { value: 'edit', label: 'Crayon', icon: <FeatherEditIcon /> },
];

// Helper function to convert ISO date string to datetime-local format
const toDateTimeLocal = (isoString: string | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  // Get local date/time components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper function to convert datetime-local value to ISO string
const fromDateTimeLocal = (localValue: string): string => {
  if (!localValue) return '';
  // datetime-local gives us YYYY-MM-DDTHH:mm in local time
  // We need to create a Date object and convert to ISO
  const date = new Date(localValue);
  return date.toISOString();
};

const CourseBuilderV2: React.FC = () => {
  const [course, setCourse] = useState<Course>({
    courseProject: '',
    title: {},
    description: {},
    defaultLanguage: 'en',
    availableLanguages: ['en'],
    isActive: true,
    coachId: '',
    chapters: [],
    tags: [],
    courseType: null
  });
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'idle'>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState('');
  const [coaches, setCoaches] = useState<Array<{ id: string; name: string }>>([]);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{ chapterIndex: number; itemIndex: number | null; item: ChapterItem | null }>({ chapterIndex: 0, itemIndex: null, item: null });
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  /** Onglets groupe dédiés au déblocage formation (indépendant du coach / chapitres) */
  const [selectedGroupUnlockId, setSelectedGroupUnlockId] = useState<string>('');
  const [mainTab, setMainTab] = useState<'general' | 'chapters' | 'groups' | 'assessment'>(
    'general'
  );

  // === Diagnostic & Impact (états déclarés ici ; callbacks définis après courseId) ===
  const [assessmentEnabled, setAssessmentEnabled] = useState<boolean>(false);
  const [assessmentDoc, setAssessmentDoc] = useState<CourseAssessment>(buildEmptyAssessment());
  const [assessmentLoading, setAssessmentLoading] = useState<boolean>(false);
  const [assessmentSavedSnapshot, setAssessmentSavedSnapshot] = useState<string>('');
  const [followupIdsWithResponses, setFollowupIdsWithResponses] = useState<Set<string>>(
    new Set()
  );

  const [chapterTabByIndex, setChapterTabByIndex] = useState<Record<number, 0 | 1>>({});
  
  const [capsuleDialogOpen, setCapsuleDialogOpen] = useState(false);
  const [editingCapsule, setEditingCapsule] = useState<ICapsule | null>(null);

  const [validationOpen, setValidationOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const runCompletenessCheck = () => {
    const result = validateCourseCompleteness(
      course,
      course.availableLanguages,
      course.defaultLanguage,
      assessmentEnabled ? assessmentDoc : undefined
    );
    setValidationResult(result);
    setValidationOpen(true);
  };

  const languageName = (code: string): string =>
    AVAILABLE_LANGUAGES.find((l) => l.code === code)?.name || code.toUpperCase();

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId } = useParams();
  const isEditing = Boolean(courseId);

  /**
   * Pendant chargements serveur / sauvegarde, on n’incrémente pas « modifications en attente ».
   * Compteur pour gérer des chargements concurrents (cours + groupes).
   */
  const suppressDirtyCountRef = useRef(0);
  /** Après save / sync serveur : ignore les pulses « dirty » (effets React en retard, GET assessment qui finit après). */
  const ignoreDirtyUntilMsRef = useRef(0);
  /** Libération du suppress un peu après les setState pour laisser React exécuter les effets pendant que suppress > 0. */
  const SUPPRESS_RELEASE_DELAY_MS = 80;

  const bumpSuppressDirty = React.useCallback(() => {
    suppressDirtyCountRef.current += 1;
  }, []);
  const releaseSuppressDirty = React.useCallback(() => {
    suppressDirtyCountRef.current = Math.max(0, suppressDirtyCountRef.current - 1);
  }, []);

  // === Diagnostic & Impact callbacks (dépendent de courseId/isEditing) ===
  const loadAssessment = React.useCallback(async (): Promise<CourseAssessmentResponseEnvelope | null> => {
    if (!isEditing || !courseId) return null;
    bumpSuppressDirty();
    setAssessmentLoading(true);
    try {
      const env = await fetchCourseAssessment(courseId);
      setAssessmentEnabled(!!env.diagnosticImpactEnabled);
      const doc = env.assessment || buildEmptyAssessment();
      setAssessmentDoc(doc);
      setAssessmentSavedSnapshot(JSON.stringify(doc));
      ignoreDirtyUntilMsRef.current = Date.now() + 280;
      try {
        const resp = await fetchAssessmentResponses(courseId);
        const ids = new Set<string>();
        for (const r of resp.responses || []) {
          if (r.measurement === 'followup' && r.followupId) ids.add(String(r.followupId));
        }
        setFollowupIdsWithResponses(ids);
      } catch {
        setFollowupIdsWithResponses(new Set());
      }
      return env;
    } catch (err: any) {
      console.error('Erreur chargement assessment:', err);
      setError(err?.message || 'Erreur de chargement du diagnostic & impact');
      return null;
    } finally {
      setAssessmentLoading(false);
      setTimeout(() => releaseSuppressDirty(), SUPPRESS_RELEASE_DELAY_MS);
    }
  }, [courseId, isEditing, bumpSuppressDirty, releaseSuppressDirty]);

  /** Toujours précharger l’assessment en édition (sinon snapshot vide → faux « dirty » et PUT destructif au save du titre seul). */
  useEffect(() => {
    if (isEditing && courseId) {
      void loadAssessment();
    }
  }, [isEditing, courseId, loadAssessment]);

  const assessmentDirty = JSON.stringify(assessmentDoc) !== assessmentSavedSnapshot;

  const handleToggleAssessmentEnabled = async (next: boolean) => {
    if (!courseId) return;
    try {
      const result = await setCourseAssessmentEnabled(courseId, next);
      setAssessmentEnabled(!!result.diagnosticImpactEnabled);
      await loadAssessment();
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du toggle diagnostic & impact');
    }
  };

  const hasCompletedInitialLoadRef = useRef(false);
  const courseRef = useRef(course);
  const hasUnsavedChangesRef = useRef(false);
  const currentPathRef = useRef('');

  useEffect(() => {
    courseRef.current = course;
  }, [course]);

  const groupsRef = useRef<Group[]>([]);
  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    (window as any).__courseBuilderHasUnsavedChanges = hasUnsavedChanges;
    return () => {
      (window as any).__courseBuilderHasUnsavedChanges = false;
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    currentPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    fetchCoaches();
    if (isEditing && courseId) {
      fetchCourse(courseId);
      fetchGroups(courseId);
    }
  }, [courseId, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      hasCompletedInitialLoadRef.current = true;
    }
  }, [isEditing]);

  /** Détection légère des modifications : pas de JSON.stringify sur tout le cours. */
  useEffect(() => {
    if (!hasCompletedInitialLoadRef.current) return;
    if (suppressDirtyCountRef.current > 0) return;
    if (Date.now() < ignoreDirtyUntilMsRef.current) return;
    setHasUnsavedChanges(true);
    setSaveStatus((prev) => (prev === 'saving' ? prev : 'idle'));
  }, [course, assessmentDoc, groups]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const onDocumentClickCapture = (event: MouseEvent) => {
      if (!hasUnsavedChangesRef.current) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const nextUrl = new URL(href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      if (nextUrl.origin !== currentUrl.origin) return;

      const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      const currentPath = currentPathRef.current || `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      if (nextPath === currentPath) return;

      const shouldLeave = window.confirm(
        'Il y a des modifications non sauvegardées. Quitter sans sauvegarder ?'
      );
      if (!shouldLeave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('click', onDocumentClickCapture, true);
    return () => document.removeEventListener('click', onDocumentClickCapture, true);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (!hasUnsavedChangesRef.current) return;
      const shouldLeave = window.confirm(
        'Il y a des modifications non sauvegardées. Quitter sans sauvegarder ?'
      );
      if (!shouldLeave) {
        window.history.pushState(null, '', currentPathRef.current || window.location.href);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const applyHydratedCourseUpdate = (updater: (prev: Course) => Course) => {
    bumpSuppressDirty();
    setCourse((prev) => updater(prev));
    queueMicrotask(() => releaseSuppressDirty());
  };

  const markCurrentStateAsSaved = () => {
    setHasUnsavedChanges(false);
    hasUnsavedChangesRef.current = false;
    ignoreDirtyUntilMsRef.current = Date.now() + 380;
  };

  const handleExitPage = () => {
    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm(
        'Il y a des modifications non sauvegardées. Quitter sans sauvegarder ?'
      );
      if (!shouldLeave) return;
    }
    navigate('/courses');
  };

  const fetchCoaches = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/coaches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const coachesData = await response.json();
        console.log('📚 Coaches récupérés:', coachesData);
        setCoaches(coachesData);
      } else {
        console.error('❌ Erreur lors de la récupération des coaches:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error fetching coaches:', error);
    }
  };

  const fetchGroups = async (id: string) => {
    bumpSuppressDirty();
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/course/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const groupsData = await response.json();
        setGroups(groupsData);
        setSelectedGroupId((prev) => {
          if (groupsData.length === 0) return '';
          if (prev && groupsData.some((g: Group) => g.id === prev)) return prev;
          return groupsData[0].id;
        });
        setSelectedGroupUnlockId((prev) => {
          if (groupsData.length === 0) return '';
          if (prev && groupsData.some((g: Group) => g.id === prev)) return prev;
          return groupsData[0].id;
        });
      }
    } catch (error) {
      console.error('❌ Error fetching groups:', error);
    } finally {
      setTimeout(() => releaseSuppressDirty(), SUPPRESS_RELEASE_DELAY_MS);
    }
  };

  const updateGroupTrainer = async (groupId: string, trainerId: string | null) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ trainerId: trainerId || null })
      });
      if (response.ok) {
        setGroups(prev => prev.map(g => g.id === groupId ? { ...g, trainer: trainerId } : g));
      }
    } catch (err) {
      console.error('Error updating group trainer:', err);
      setError('Erreur lors de la mise à jour du formateur');
    }
  };

  const updateGroupFollowupDate = async (
    groupId: string,
    followupId: string,
    unlockDate: string | null
  ) => {
    const group = groups.find((g) => g.id === groupId);
    const current = group?.followupImpactDates || [];
    let next: Array<{ followupId: string; unlockDate: string }>;
    if (!unlockDate) {
      next = current.filter((d) => d.followupId !== followupId);
    } else {
      const exists = current.some((d) => d.followupId === followupId);
      next = exists
        ? current.map((d) => (d.followupId === followupId ? { followupId, unlockDate } : d))
        : [...current, { followupId, unlockDate }];
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ followupImpactDates: next })
      });
      if (response.ok) {
        setGroups((prev) =>
          prev.map((g) => (g.id === groupId ? { ...g, followupImpactDates: next } : g))
        );
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Erreur lors de la mise à jour de la date de mesure de suivi');
      }
    } catch (err) {
      console.error('Error updating followup date:', err);
      setError('Erreur lors de la mise à jour de la date de mesure de suivi');
    }
  };

  const updateGroupCourseUnlock = async (groupId: string, courseUnlockDate: string | null) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          courseUnlockDate: courseUnlockDate && courseUnlockDate !== '' ? courseUnlockDate : null
        })
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(prev =>
          prev.map(g =>
            g.id === groupId
              ? { ...g, courseUnlockDate: data.courseUnlockDate ?? null }
              : g
          )
        );
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Erreur lors de la mise à jour de la date de déblocage');
      }
    } catch (err) {
      console.error('Error updating course unlock date:', err);
      setError('Erreur lors de la mise à jour de la date de déblocage');
    }
  };

  const updateGroupCertificateDates = async (
    groupId: string,
    field: 'certificateStartDate' | 'certificateEndDate',
    value: string | null
  ) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value && value !== '' ? value : null })
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(prev =>
          prev.map(g =>
            g.id === groupId ? { ...g, [field]: data[field] ?? null } : g
          )
        );
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Erreur lors de la mise à jour des dates du certificat');
      }
    } catch (err) {
      console.error('Error updating certificate dates:', err);
      setError('Erreur lors de la mise à jour des dates du certificat');
    }
  };

  const handleGroupWelcomeMessageChange = (
    groupId: string,
    welcomeMessage: { [key: string]: string }
  ) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, welcomeMessage } : g))
    );
  };

  const saveGroupsWelcomeMessages = async () => {
    if (!isEditing || groups.length === 0) return;
    const token = localStorage.getItem('admin_token');
    await Promise.all(
      groups.map(async (group) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${group.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ welcomeMessage: group.welcomeMessage ?? null }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            typeof err.error === 'string'
              ? err.error
              : `Erreur lors de la sauvegarde du mot de bienvenue (${group.name})`
          );
        }
      })
    );
  };

  const getChapterOverride = (groupId: string, chapterId: string) => {
    const group = groups.find(g => g.id === groupId);
    const overrides = group?.chapterOverrides || [];
    const o = overrides.find((x: ChapterOverride) => x.chapter === chapterId);
    return o ? { deadline: o.deadline, unlockDate: o.unlockDate } : { deadline: undefined, unlockDate: undefined };
  };

  const updateGroupChapterOverrides = async (groupId: string, chapterId: string, payload: { deadline?: string; unlockDate?: string }) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const overrides = [...(group.chapterOverrides || [])];
    const idx = overrides.findIndex((x: ChapterOverride) => x.chapter === chapterId);
    const next = {
      chapter: chapterId,
      deadline: payload.deadline,
      unlockDate: payload.unlockDate
    };
    if (idx >= 0) {
      overrides[idx] = { ...overrides[idx], ...next };
    } else {
      overrides.push(next);
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ chapterOverrides: overrides })
      });
      if (response.ok) {
        setGroups(prev => prev.map(g => g.id === groupId ? { ...g, chapterOverrides: overrides } : g));
      }
    } catch (err) {
      console.error('Error updating chapter overrides:', err);
      setError('Erreur lors de la mise à jour des dates de la section');
    }
  };

  const getWorkshopOverride = (groupId: string, chapterId: string, itemId: string, baseWorkshop: IWorkshop): IWorkshop | null => {
    const group = groups.find(g => g.id === groupId);
    const overrides = group?.workshopOverrides || [];
    const o = overrides.find((x: WorkshopOverride) => x.chapter === chapterId && x.itemId === itemId);
    if (!o) return null;
    return {
      ...baseWorkshop,
      // Keep order from baseWorkshop (do NOT reset to 0)
      title: o.title ?? baseWorkshop.title,
      description: o.description ?? baseWorkshop.description,
      image: o.image ?? baseWorkshop.image,
      date: o.date ? o.date.split('T')[0] : baseWorkshop.date,
      startTime: o.startTime ?? baseWorkshop.startTime,
      endTime: o.endTime ?? baseWorkshop.endTime,
      details: o.details ?? baseWorkshop.details,
      lieu: o.lieu ?? baseWorkshop.lieu,
      distanciel: o.distanciel ?? baseWorkshop.distanciel
    };
  };

  const updateGroupWorkshopOverride = async (groupId: string, chapterId: string, itemId: string, data: IWorkshop & { itemType: 'workshop' }) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const overrides = [...(group.workshopOverrides || [])];
    const idx = overrides.findIndex((x: WorkshopOverride) => x.chapter === chapterId && x.itemId === itemId);
    const next: WorkshopOverride = {
      chapter: chapterId,
      itemId,
      title: data.title,
      description: data.description,
      image: data.image,
      date: data.date ? new Date(data.date).toISOString() : undefined,
      startTime: data.startTime,
      endTime: data.endTime,
      details: data.details,
      lieu: data.lieu,
      distanciel: data.distanciel
    };
    if (idx >= 0) overrides[idx] = next;
    else overrides.push(next);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ workshopOverrides: overrides })
      });
      if (response.ok) {
        setGroups(prev => prev.map(g => g.id === groupId ? { ...g, workshopOverrides: overrides } : g));
      }
    } catch (err) {
      console.error('Error updating workshop override:', err);
      setError('Erreur lors de la sauvegarde de l\'atelier');
    }
  };

  const deleteGroupById = async (groupId: string) => {
    if (!courseId) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchGroups(courseId);
      } else {
        const err = await response.json();
        setError(err.error || 'Impossible de supprimer le groupe');
      }
    } catch (err) {
      console.error('Error deleting group:', err);
      setError('Erreur lors de la suppression du groupe');
    }
  };

  const fetchCourse = async (id: string) => {
    bumpSuppressDirty();
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const courseData = await response.json();
        console.log('📊 Données reçues du backend:', courseData);
        console.log('🌐 availableLanguages reçues:', courseData.availableLanguages);
        console.log('🌐 defaultLanguage reçue:', courseData.defaultLanguage);
        console.log('🖼️ Image reçue:', courseData.image);
        
        const transformedCourse: Course = {
          id: courseData.id,
          courseProject: courseData.courseProject || '',
          title: courseData.title || { en: courseData.title },
          description: courseData.description || { en: courseData.description },
          image: courseData.image,
          defaultLanguage: courseData.defaultLanguage || 'en',
          availableLanguages: courseData.availableLanguages || ['en'],
          isActive: courseData.isActive,
          coachId: courseData.coach?.id || '',
          chapters: [],
          tags: Array.isArray(courseData.tags) ? courseData.tags : [],
          courseType: courseData.courseType ?? null
        };
        
        console.log('🎯 availableLanguages finales:', transformedCourse.availableLanguages);
        console.log('🎯 defaultLanguage finale:', transformedCourse.defaultLanguage);
        applyHydratedCourseUpdate(() => transformedCourse);

        // Fetch chapters
        const chaptersResponse = await fetch(`${process.env.REACT_APP_API_URL}/chapters/course/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (chaptersResponse.ok) {
          const chaptersData = await chaptersResponse.json();
          console.log('📖 Chapitres reçus du backend:', chaptersData);
          
          // Filtrer et transformer les chapitres valides
          const transformedChapters = chaptersData
            .filter((chapter: any) => {
              // Vérifier que le chapitre a au moins un titre dans une langue
              const hasTitle = chapter.title && typeof chapter.title === 'object' && Object.keys(chapter.title).length > 0;
              if (!hasTitle) {
                console.warn('⚠️ Chapitre ignoré (pas de titre):', chapter);
              }
              return hasTitle;
            })
            .map((chapter: any) => ({
              id: chapter._id,
              _id: chapter._id,
              title: chapter.title || {},
              description: chapter.description || {},
              summary: chapter.summary || {},
              order: chapter.order,
              icon: chapter.icon || undefined,
              requiresRating: chapter.requiresRating !== undefined ? chapter.requiresRating : true,
              items: (chapter.items || []).map((item: any) => {
                if (item.itemType === 'capsule') {
                  console.log('📦 Capsule chargée:', item.title, 'Image:', item.image);
                }
                if (item.itemType === 'workshop' && item.date) {
                  return { ...item, date: item.date.split('T')[0] };
                }
                return ensureQuizQuestionIds(item);
              })
            }));
          applyHydratedCourseUpdate((prev) => ({ ...prev, chapters: transformedChapters }));
          markCurrentStateAsSaved();
          setSaveStatus('saved');
          hasCompletedInitialLoadRef.current = true;
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      setError('Error loading course data');
    } finally {
      setTimeout(() => releaseSuppressDirty(), SUPPRESS_RELEASE_DELAY_MS);
    }
  };

  const saveChapters = async (chapters: any[], silent: boolean = false, markAsSaved: boolean = false) => {
    console.log('📚 saveChapters appelée, silent:', silent);
    console.log('📦 Chapitres à sauvegarder:', chapters);
    
    // Vérifier les images des capsules et workshops
    chapters.forEach((chapter, chIndex) => {
      chapter.items?.forEach((item: any, itemIndex: number) => {
        if (item.itemType === 'capsule' && item.image) {
          console.log(`🖼️ Capsule [${chIndex}][${itemIndex}] a une image:`, item.image);
        }
        if (item.itemType === 'workshop' && item.image) {
          console.log(`🖼️ Workshop [${chIndex}][${itemIndex}] a une image:`, item.image);
        }
      });
    });
    
    if (!silent) setSaveStatus('saving');
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/chapters/bulk`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          courseId: courseId,
          chapters: serializeChaptersQuizItemsForBackend(chapters)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Impossible de sauvegarder les sections');
      }

      const result = await response.json();
      console.log('✅ Chapitres sauvegardés avec succès');
      console.log('📦 Chapitres retournés par le backend:', result.chapters);
      
      // Update chapters with the data returned from backend (including new _id for newly created chapters)
      // Only update if we have chapters returned and we're not in the middle of adding a new chapter
      if (result.chapters && Array.isArray(result.chapters)) {
        console.log('🔄 Mise à jour des chapitres depuis le backend:', result.chapters.length, 'chapitres reçus');
        applyHydratedCourseUpdate(prev => {
          console.log('📋 Chapitres locaux avant mise à jour:', prev.chapters.length);
          prev.chapters.forEach((ch, idx) => {
            console.log(`  Chapitre local ${idx}: id=${ch.id}, _id=${ch._id || 'NO_ID'}, order=${ch.order}, hasTitle=${Object.keys(ch.title || {}).length > 0}`);
          });
          
          // Check if there are any local chapters without _id (newly added, not yet saved)
          const newLocalChapters = prev.chapters.filter(ch => !ch._id);
          
          // If we have new chapters locally, merge them with backend data instead of replacing
          // This preserves new chapters even if they don't have a valid title yet
          if (newLocalChapters.length > 0) {
            console.log(`⚠️ ${newLocalChapters.length} chapitre(s) local(aux) sans _id détecté(s), fusion avec les données du backend`);
            const backendChaptersMap = new Map<string, any>(
              result.chapters.map((ch: any) => [ch._id?.toString(), ch])
            );
            
            // Merge: keep local chapters without _id, update others with backend data
            const mergedChapters = prev.chapters.map(localChapter => {
              if (!localChapter._id) {
                // Keep new local chapter as-is
                return localChapter;
              }
              
              // Update existing chapter with backend data
              const backendChapter: any = backendChaptersMap.get(localChapter._id);
              if (backendChapter) {
                return {
                  id: backendChapter._id?.toString() || backendChapter.id,
                  _id: backendChapter._id?.toString() || backendChapter._id,
                  title: backendChapter.title || {},
                  description: backendChapter.description || {},
                  summary: backendChapter.summary || {},
                  order: backendChapter.order,
                  icon: backendChapter.icon,
                  requiresRating: backendChapter.requiresRating !== undefined ? backendChapter.requiresRating : true,
                  items: (backendChapter.items || []).map((item: any) => {
                    if (item.itemType === 'workshop') {
                      const workshopItem = { ...item };
                      if (item.date) {
                        workshopItem.date = item.date.split('T')[0];
                      }
                      // Preserve image field explicitly
                      if (item.image) {
                        workshopItem.image = item.image;
                      }
                      return workshopItem;
                    }
                    return ensureQuizQuestionIds(item);
                  })
                };
              }
              
              return localChapter;
            });
            
            // Add any new chapters from backend that weren't in local state
            result.chapters.forEach((backendChapter: any) => {
              const backendId = backendChapter?._id?.toString();
              if (backendId && !mergedChapters.some(ch => ch._id === backendId)) {
                mergedChapters.push({
                  id: backendId,
                  _id: backendId,
                  title: backendChapter?.title || {},
                  description: backendChapter?.description || {},
                  summary: backendChapter?.summary || {},
                  order: backendChapter?.order || 0,
                  icon: backendChapter?.icon,
                  requiresRating: backendChapter?.requiresRating !== undefined ? backendChapter.requiresRating : true,
                  items: (backendChapter?.items || []).map((item: any) => {
                    if (item.itemType === 'workshop') {
                      const workshopItem = { ...item };
                      if (item.date) {
                        workshopItem.date = item.date.split('T')[0];
                      }
                      // Preserve image field explicitly
                      if (item.image) {
                        workshopItem.image = item.image;
                      }
                      return workshopItem;
                    }
                    return ensureQuizQuestionIds(item);
                  })
                });
              }
            });
            
            return { ...prev, chapters: mergedChapters };
          }
          
          // No new local chapters, safe to replace with backend data
          const updatedChapters = result.chapters.map((chapter: any) => {
            const chapterData = {
              id: chapter._id?.toString() || chapter.id,
              _id: chapter._id?.toString() || chapter._id,
              title: chapter.title || {},
              description: chapter.description || {},
              summary: chapter.summary || {},
              order: chapter.order,
              icon: chapter.icon,
              requiresRating: chapter.requiresRating !== undefined ? chapter.requiresRating : true,
              items: (chapter.items || []).map((item: any) => {
                if (item.itemType === 'workshop') {
                  const workshopItem = { ...item };
                  if (item.date) {
                    workshopItem.date = item.date.split('T')[0];
                  }
                  // Preserve image field explicitly
                  if (item.image) {
                    workshopItem.image = item.image;
                  }
                  return workshopItem;
                }
                return ensureQuizQuestionIds(item);
              })
            };
            return chapterData;
          });
          
          return { ...prev, chapters: updatedChapters };
        });
      }
      
      if (!silent) setSaveStatus('saved');
      if (markAsSaved) {
        markCurrentStateAsSaved();
      }
      return result;
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde des chapitres:', error);
      if (!silent) {
        setError(error.message);
        setSaveStatus('error');
      }
      throw error;
    }
  };

  const saveCourse = async (silent: boolean = false) => {
    console.log('🚀 saveCourse appelée, silent:', silent);
    bumpSuppressDirty();
    if (!silent) setSaveStatus('saving');
    setError('');

    try {
      // Validate
      if (!course.title[course.defaultLanguage]) {
        throw new Error('Le titre de la formation est requis dans la langue par défaut');
      }

      // Coach is configured at group level in edition.

      const courseData = {
        courseProject: course.courseProject,
        title: course.title,
        // In create mode we intentionally hide the description form,
        // so we derive it from the title to satisfy backend validation.
        description:
          course.description && Object.keys(course.description).length > 0
            ? course.description
            : course.title,
        image: course.image,
        defaultLanguage: course.defaultLanguage,
        availableLanguages: course.availableLanguages,
        isActive: course.isActive,
        tags: course.tags,
        courseType: course.courseType ?? null,
        ...(user?.role === 'admin' && course.coachId ? { coachId: course.coachId } : {})
      };

      console.log('💾 Données envoyées au backend:', courseData);
      console.log('🌐 availableLanguages envoyées:', courseData.availableLanguages);
      console.log('🌐 defaultLanguage envoyée:', courseData.defaultLanguage);
      console.log('🖼️ Image envoyée:', courseData.image);

      const token = localStorage.getItem('admin_token');
      const url = isEditing ? `${process.env.REACT_APP_API_URL}/courses/${courseId}` : `${process.env.REACT_APP_API_URL}/courses`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(courseData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Erreur backend:', errorData);
        const errorMessage = typeof errorData.error === 'string' 
          ? errorData.error 
          : JSON.stringify(errorData.error) || 'Impossible de sauvegarder la formation';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      const savedCourseId = isEditing ? courseId : result.course.id;

      // If creating a new course, update the URL
      if (!isEditing) {
        navigate(`/courses/${savedCourseId}/edit`, { replace: true });
        applyHydratedCourseUpdate((prev) => ({ ...prev, id: savedCourseId }));
      }

      const chaptersSnapshot = mergePendingItemDialogIntoChapters(course.chapters);
      if (chaptersSnapshot !== course.chapters) {
        setCourse((prev) => ({ ...prev, chapters: chaptersSnapshot }));
        setItemDialogOpen(false);
        setEditingItem({ chapterIndex: 0, itemIndex: null, item: null });
      }

      // Save chapters (filter out empty chapters)
      if (chaptersSnapshot.length > 0) {
        console.log('📚 Tous les chapitres avant filtrage:', chaptersSnapshot);
        
        // Filtrer les chapitres valides (avec au moins un titre dans une langue)
        const validChapters = chaptersSnapshot.filter(chapter => {
          const hasValidTitle = chapter.title && 
                               typeof chapter.title === 'object' && 
                               Object.keys(chapter.title).length > 0 &&
                               Object.values(chapter.title).some(val => val && val.trim() !== '');
          const hasContent = Array.isArray(chapter.items) && chapter.items.length > 0;
          
          if (!hasValidTitle && !hasContent) {
            console.warn('⚠️ Chapitre ignoré lors de la sauvegarde (pas de titre et pas de contenu):', chapter);
          }
          
          // Ne jamais ignorer un chapitre qui contient déjà des items (sinon risque de perte de données).
          return hasValidTitle || hasContent;
        });

        console.log('✅ Chapitres valides après filtrage:', validChapters.length, 'sur', chaptersSnapshot.length);

        if (validChapters.length > 0) {
          const chaptersData = validChapters.map(chapter => {
            console.log('📋 Préparation du chapitre:', chapter.title, '_id:', chapter._id || chapter.id);
            console.log('📝 Summary du chapitre:', chapter.summary);
            
            // Only include _id if it's a valid MongoDB ObjectId (24 hex characters)
            // New chapters have an id generated by Date.now() which is not a valid ObjectId
            const isValidObjectId = chapter._id && /^[0-9a-fA-F]{24}$/.test(chapter._id.toString());
            const chapterData: any = {
              title: chapter.title,
              description: chapter.description,
              summary: chapter.summary,
              order: chapter.order,
              icon: chapter.icon,
              requiresRating: chapter.requiresRating !== undefined ? chapter.requiresRating : true,
              items: chapter.items.map((item: any) => {
                const itemData: any = { ...item }; // Spread operator preserves all fields including _id and image
                if (item.itemType === 'workshop' && item.date) {
                  itemData.date = new Date(item.date).toISOString();
                }
                // Ensure image is preserved for workshops
                if (item.itemType === 'workshop' && item.image) {
                  itemData.image = item.image;
                  console.log(`🖼️ Workshop image préservée lors de l'envoi:`, item.image);
                }
                return itemData;
              })
            };
            
            // Only add _id if it's a valid MongoDB ObjectId (existing chapter)
            if (isValidObjectId) {
              chapterData._id = chapter._id;
              console.log('📤 Chapitre existant préparé pour envoi (avec _id):', chapterData);
            } else {
              console.log('📤 Nouveau chapitre préparé pour envoi (sans _id):', chapterData);
            }
            
            return chapterData;
          });

          console.log('📦 Envoi de', chaptersData.length, 'chapitres au backend');
          await saveChapters(chaptersData, true, true); // silent save
        } else {
          console.warn('⚠️ Aucun chapitre valide à sauvegarder');
        }
      }

      // === Diagnostic & Impact : sauvegarde branchée sur le flux principal ===
      let assessmentPayload: CourseAssessment = assessmentDoc;
      let snapshotBaseline = assessmentSavedSnapshot;

      if (isEditing && courseId && assessmentSavedSnapshot === '') {
        const env = await loadAssessment();
        if (env?.assessment) {
          assessmentPayload = env.assessment;
          snapshotBaseline = JSON.stringify(env.assessment);
        }
      }

      const assessmentIsDirty =
        JSON.stringify(assessmentPayload) !== snapshotBaseline &&
        (!isEditing || !courseId || snapshotBaseline !== '');

      if (assessmentIsDirty && (isEditing ? courseId : savedCourseId)) {
        const validationError = validateAssessment(assessmentPayload);
        if (validationError) {
          throw new Error(validationError);
        }
        const env = await updateCourseAssessment(
          (isEditing ? courseId : savedCourseId) as string,
          {
            linkedQuestions: assessmentPayload.linkedQuestions,
            initialDiagnosticDescription: assessmentPayload.initialDiagnosticDescription,
            initialDiagnosticQuestions: assessmentPayload.initialDiagnosticQuestions,
            finalImpactDescription: assessmentPayload.finalImpactDescription,
            finalImpactQuestions: assessmentPayload.finalImpactQuestions,
            followupImpacts: assessmentPayload.followupImpacts,
          }
        );
        const fresh = env.assessment || buildEmptyAssessment();
        setAssessmentDoc(fresh);
        setAssessmentSavedSnapshot(JSON.stringify(fresh));
        setAssessmentEnabled(!!env.diagnosticImpactEnabled);
      }

      await saveGroupsWelcomeMessages();

      markCurrentStateAsSaved();
      setSaveStatus('saved');
      if (!silent) {
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
    } catch (error: any) {
      setError(error.message);
      setSaveStatus('error');
    } finally {
      setTimeout(() => releaseSuppressDirty(), SUPPRESS_RELEASE_DELAY_MS);
    }
  };

  const handleLanguageToggle = (langCode: string) => {
    console.log('🔄 handleLanguageToggle appelée pour:', langCode);
    console.log('🌐 Langues actuelles:', course.availableLanguages);
    
    const isSelected = course.availableLanguages.includes(langCode);
    console.log('✅ Langue sélectionnée:', isSelected);
    
    if (isSelected && course.availableLanguages.length === 1) {
      console.log('⚠️ Tentative de supprimer la dernière langue - bloquée');
      return;
    }

    let newLanguages = isSelected
      ? course.availableLanguages.filter(l => l !== langCode)
      : [...course.availableLanguages, langCode];

    let newDefaultLanguage = course.defaultLanguage;
    if (!newLanguages.includes(course.defaultLanguage)) {
      newDefaultLanguage = newLanguages[0];
      console.log('🔄 Langue par défaut changée vers:', newDefaultLanguage);
    }

    console.log('🎯 Nouvelles langues:', newLanguages);
    console.log('🎯 Nouvelle langue par défaut:', newDefaultLanguage);

    setCourse(prev => ({ ...prev, availableLanguages: newLanguages, defaultLanguage: newDefaultLanguage }));
    
  };

  const addChapter = async () => {
    if (!isEditing || !courseId) {
      // If not editing, just add locally (will be created when course is saved)
      const newChapter: Chapter = {
        id: Date.now().toString(),
        title: {},
        description: {},
        order: course.chapters.length + 1,
        requiresRating: true,
        items: []
      };
      setCourse(prev => ({ ...prev, chapters: [...prev.chapters, newChapter] }));
      return;
    }

    // If editing, create chapter in backend immediately to get a real MongoDB _id
    try {
      const token = localStorage.getItem('admin_token');
      const order = course.chapters.length + 1;
      
      // Create chapter with empty title (will be updated when user fills it)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/chapters`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          courseId: courseId,
          title: { [course.defaultLanguage]: 'New Chapter' }, // Temporary title
          description: {},
          order: order,
          requiresRating: true,
          items: []
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Impossible de créer la section');
      }

      const result = await response.json();
      const newChapter = result.chapter;
      
      // Add the new chapter to local state with the MongoDB _id
      setCourse(prev => ({
        ...prev,
        chapters: [...prev.chapters, {
          id: newChapter._id,
          _id: newChapter._id,
          title: newChapter.title || {},
          description: newChapter.description || {},
              summary: newChapter.summary || {},
          order: newChapter.order,
          icon: newChapter.icon,
          requiresRating: newChapter.requiresRating !== undefined ? newChapter.requiresRating : true,
          items: newChapter.items || []
        }]
      }));
    } catch (error: any) {
      console.error('Error creating chapter:', error);
      setError(error.message || 'Impossible de créer la section');
    }
  };

  const updateChapter = (index: number, updates: Partial<Chapter>) => {
    setCourse(prev => ({
      ...prev,
      chapters: prev.chapters.map((chapter, i) => i === index ? { ...chapter, ...updates } : chapter)
    }));
  };

  const moveChapter = (index: number, direction: 'up' | 'down') => {
    setCourse(prev => {
      const chapters = [...prev.chapters];
      if (direction === 'up' && index > 0) {
        [chapters[index - 1], chapters[index]] = [chapters[index], chapters[index - 1]];
      } else if (direction === 'down' && index < chapters.length - 1) {
        [chapters[index], chapters[index + 1]] = [chapters[index + 1], chapters[index]];
      } else {
        return prev;
      }
      const reordered = chapters.map((ch, i) => ({ ...ch, order: i + 1 }));
      return { ...prev, chapters: reordered };
    });
  };

  const deleteChapter = async (index: number) => {
    const chapterToDelete = course.chapters[index];
    // Suppression optimiste côté UI
    setCourse(prev => ({ ...prev, chapters: prev.chapters.filter((_, i) => i !== index) }));

    // Si on est en édition et que le chapitre existe en base, appeler l'API
    if (isEditing && courseId && chapterToDelete && chapterToDelete._id) {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/chapters/${chapterToDelete._id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Error deleting chapter from backend:', response.status, errorData);
          setError(errorData.error || 'Erreur lors de la suppression de la section');
          // En cas d’échec, on pourrait recharger les chapitres depuis le backend si besoin
        }
      } catch (err: any) {
        console.error('Error deleting chapter:', err);
        setError('Erreur lors de la suppression de la section');
      }
    }
  };

  const openAddItemDialog = (chapterIndex: number, type: 'capsule' | 'quiz' | 'workshop') => {
    const chapter = course.chapters[chapterIndex];
    const order = chapter.items.length + 1;

    if (type === 'capsule') {
      // Pour les capsules, ouvrir directement l'éditeur avancé
      const newCapsule = { 
        order, 
        title: {}, 
        description: {}, 
        modules: [], 
        estimatedDuration: 0,
        isActive: true 
      };
      setEditingCapsule(newCapsule);
      setEditingItem({ chapterIndex, itemIndex: null, item: { itemType: 'capsule', ...newCapsule } });
      setCapsuleDialogOpen(true);
    } else {
      // Pour les autres types (quiz, workshop), utiliser le dialog normal
      let newItem: ChapterItem;
      if (type === 'quiz') {
        newItem = { itemType: 'quiz', order, title: {}, description: {}, questions: [], isDiagnostic: false };
      } else {
        newItem = { itemType: 'workshop', order, title: {}, description: {}, date: '', startTime: '', endTime: '' };
      }
      setEditingItem({ chapterIndex, itemIndex: null, item: newItem });
      setItemDialogOpen(true);
    }
  };

  const openEditItemDialog = (chapterIndex: number, itemIndex: number) => {
    const chapter = course.chapters[chapterIndex];
    const item = chapter.items[itemIndex];
    const chapterId = chapter.id || (chapter as any)._id || '';

    // Pour les capsules, ouvrir directement l'éditeur avancé
    if (item.itemType === 'capsule') {
      const capsuleItem = item as ICapsule & { itemType: 'capsule' };
      setEditingCapsule({
        order: capsuleItem.order,
        title: capsuleItem.title || {},
        description: capsuleItem.description || {},
        image: capsuleItem.image,
        modules: capsuleItem.modules || [],
        estimatedDuration: capsuleItem.estimatedDuration ?? capsuleItem.duration ?? undefined,
        isActive: capsuleItem.isActive || true
      });
      setEditingItem({ chapterIndex, itemIndex, item: { ...item } });
      setCapsuleDialogOpen(true);
    } else {
      let itemCopy: ChapterItem = { ...item };
      if (item.itemType === 'workshop') {
        const workshopItem = item as IWorkshop & { itemType: 'workshop' };
        if (workshopItem.image) (itemCopy as any).image = workshopItem.image;
        // En mode groupe : préremplir avec l’override du groupe ou la base du chapitre
        if (selectedGroupId && chapterId) {
          const itemId = (item as any)._id || (item as any).id;
          if (itemId) {
            const override = getWorkshopOverride(selectedGroupId, chapterId, itemId, workshopItem);
            if (override) {
              itemCopy = { ...override, itemType: 'workshop' } as ChapterItem;
              if ((override as any).image) (itemCopy as any).image = (override as any).image;
            }
          }
        }
      }
      if (item.itemType === 'quiz') {
        itemCopy = ensureQuizQuestionIds(itemCopy);
      }
      setEditingItem({ chapterIndex, itemIndex, item: itemCopy });
      setItemDialogOpen(true);
    }
  };

  const prepareItemForChapterSave = (
    item: ChapterItem,
    originalItem: ChapterItem | null | undefined
  ): ChapterItem => {
    const originalId =
      originalItem && '_id' in originalItem ? (originalItem as { _id?: string })._id : undefined;

    let itemToSave: ChapterItem = {
      ...item,
      ...(originalId ? { _id: originalId } : {}),
    } as ChapterItem;

    if (item.itemType === 'workshop') {
      const workshopItem = item as IWorkshop & { itemType: 'workshop' };
      if (workshopItem.image) (itemToSave as any).image = workshopItem.image;
    }

    if (item.itemType === 'quiz') {
      itemToSave = ensureQuizQuestionIds(itemToSave);
      if (itemToSave.itemType === 'quiz') {
        itemToSave = {
          ...itemToSave,
          questions: itemToSave.questions.map((q, idx) => ({ ...q, order: idx + 1 })),
        };
      }
    }

    return itemToSave;
  };

  const mergePendingItemDialogIntoChapters = (chapters: Chapter[]): Chapter[] => {
    if (!itemDialogOpen || !editingItem.item) return chapters;

    const { chapterIndex, itemIndex, item } = editingItem;
    const chapter = chapters[chapterIndex];
    if (!chapter) return chapters;

    const originalItem = itemIndex !== null ? chapter.items[itemIndex] : null;
    const itemToSave = prepareItemForChapterSave(item, originalItem);
    const updatedChapters = [...chapters];
    const updatedChapter = { ...updatedChapters[chapterIndex] };

    if (itemIndex === null) {
      updatedChapter.items = [...updatedChapter.items, itemToSave];
    } else {
      updatedChapter.items = [...updatedChapter.items];
      updatedChapter.items[itemIndex] = itemToSave;
    }

    updatedChapters[chapterIndex] = updatedChapter;
    return updatedChapters;
  };

  const saveItemFromDialog = async () => {
    if (!editingItem.item) return;

    const { chapterIndex, itemIndex, item } = editingItem;
    const chapter = course.chapters[chapterIndex];
    const chapterId = chapter.id || (chapter as any)._id || '';

    const originalItem = itemIndex !== null ? chapter.items[itemIndex] : null;
    const itemToSave = prepareItemForChapterSave(item, originalItem);

    // Atelier + groupe sélectionné : sauvegarder dans les overrides du groupe
    if (item.itemType === 'workshop' && selectedGroupId && itemIndex !== null) {
      const itemId = (item as any)._id || (item as any).id;
      if (itemId) {
        await updateGroupWorkshopOverride(
          selectedGroupId,
          chapterId,
          itemId,
          itemToSave as IWorkshop & { itemType: 'workshop' }
        );
        setItemDialogOpen(false);
        setEditingItem({ chapterIndex: 0, itemIndex: null, item: null });
        return;
      }
    }

    const updatedChapters = [...course.chapters];
    const updatedChapter = { ...updatedChapters[chapterIndex] };

    if (itemIndex === null) {
      updatedChapter.items = [...updatedChapter.items, itemToSave];
    } else {
      updatedChapter.items = [...updatedChapter.items];
      updatedChapter.items[itemIndex] = itemToSave;
    }
    updatedChapters[chapterIndex] = updatedChapter;

    setCourse((prev) => ({ ...prev, chapters: updatedChapters }));
    setItemDialogOpen(false);
    setEditingItem({ chapterIndex: 0, itemIndex: null, item: null });

    if (isEditing && courseId) {
      try {
        await saveChapters(updatedChapters, true, true);
      } catch (error: any) {
        console.error('❌ Erreur lors de la sauvegarde du quiz:', error);
        setError(error.message || 'Erreur lors de la sauvegarde du quiz');
      }
    }
  };

  const updateItemInDialog = (updatedItem: ICapsule | IQuiz | IWorkshop) => {
    if (editingItem.item) {
      const itemWithType = { ...updatedItem, itemType: editingItem.item.itemType } as ChapterItem;
      setEditingItem(prev => ({ ...prev, item: itemWithType }));
    }
  };

  const moveItem = (chapterIndex: number, itemIndex: number, direction: 'up' | 'down') => {
    const chapter = course.chapters[chapterIndex];
    const items = [...chapter.items];
    
    if (direction === 'up' && itemIndex > 0) {
      [items[itemIndex - 1], items[itemIndex]] = [items[itemIndex], items[itemIndex - 1]];
      items[itemIndex - 1].order = itemIndex;
      items[itemIndex].order = itemIndex + 1;
    } else if (direction === 'down' && itemIndex < items.length - 1) {
      [items[itemIndex], items[itemIndex + 1]] = [items[itemIndex + 1], items[itemIndex]];
      items[itemIndex].order = itemIndex + 1;
      items[itemIndex + 1].order = itemIndex + 2;
    }
    
    updateChapter(chapterIndex, { items });
  };

  const deleteItem = (chapterIndex: number, itemIndex: number) => {
    const chapter = course.chapters[chapterIndex];
    updateChapter(chapterIndex, { items: chapter.items.filter((_, i) => i !== itemIndex) });
  };

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'quiz': return <QuizIcon fontSize="small" />;
      case 'workshop': return <WorkshopIcon fontSize="small" />;
      default: return <CapsuleIcon fontSize="small" />;
    }
  };

  const updateWorkshopOverrideField = async (
    groupId: string,
    chapterId: string,
    workshopItem: IWorkshop & { itemType: 'workshop' },
    patch: Partial<IWorkshop>
  ) => {
    const itemId = (workshopItem as any)._id || (workshopItem as any).id;
    if (!itemId) return;
    const effective = getWorkshopOverride(groupId, chapterId, itemId, workshopItem) || workshopItem;
    await updateGroupWorkshopOverride(groupId, chapterId, itemId, {
      ...(effective as IWorkshop),
      ...(patch as IWorkshop),
      itemType: 'workshop'
    });
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        pb: hasUnsavedChanges ? 10 : 0,
        '& .MuiInputLabel-root': {
          fontWeight: 700,
          fontSize: '0.95rem',
        },
        '& .MuiFormControlLabel-label': {
          fontWeight: 700,
          fontSize: '0.95rem',
        },
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={handleExitPage}>
              <BackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight="bold">
              {isEditing ? 'Modifier la formation' : 'Créer une nouvelle formation'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" ml={7}>
            {isEditing && (
              <>
                {saveStatus === 'saving' && <CircularProgress size={14} sx={{ mr: 1 }} />}
                {saveStatus === 'saved' && <SavedIcon sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />}
                {saveStatus === 'saving' && 'Sauvegarde...'}
                {saveStatus === 'saved' && 'Toutes les modifications sont sauvegardées'}
                {saveStatus === 'error' && 'Erreur de sauvegarde'}
                {saveStatus === 'idle' && (hasUnsavedChanges ? 'Modifications non sauvegardées' : 'Aucune modification en attente')}
              </>
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={<ValidateIcon />}
            onClick={runCompletenessCheck}
          >
            Vérifier la complétude
          </Button>
          {!isEditing && (
            <Button
              variant="contained"
              onClick={() => saveCourse(false)}
              disabled={
                !course.title[course.defaultLanguage]
              }
            >
              Ajouter
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {hasUnsavedChanges && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 -6px 18px rgba(0,0,0,0.12)',
            px: 3,
            py: 1.5,
          }}
        >
          <Box maxWidth="lg" mx="auto" display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Des modifications sont en attente de sauvegarde.
            </Typography>
            <Button
              variant="contained"
              onClick={() => saveCourse(false)}
              disabled={saveStatus === 'saving' || !course.title[course.defaultLanguage]}
            >
              {saveStatus === 'saving' ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </Box>
        </Box>
      )}

      <Card
          sx={{
            mb: 0,
            borderRadius: '8px 8px 0 0',
            borderBottom: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Tabs
              value={mainTab}
              onChange={(_, v) => v && setMainTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40 }}
            >
              <Tab label="Général" value="general" sx={{ minHeight: 40 }} />
              <Tab label="Sections" value="chapters" sx={{ minHeight: 40 }} />
              <Tab label="Groupes" value="groups" sx={{ minHeight: 40 }} />
              <Tab label="Diagnostic & Impact" value="assessment" sx={{ minHeight: 40 }} />
            </Tabs>
          </CardContent>
        </Card>
      {/* Gestion des groupes — première section en mode édition */}
      {mainTab === 'groups' && isEditing && courseId && (
        <Card
          sx={{
            mb: groups.length > 0 ? 0 : 3,
            mt: 0,
            borderRadius: groups.length > 0 ? '0' : '0 0 8px 8px',
            borderTop: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="600">
              <GroupIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gestion des groupes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sélectionnez un groupe pour éditer ses paramètres (formateur, dates des sections, ateliers) ci-dessous.
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
              <Tabs
                value={selectedGroupId || false}
                onChange={(_, v) => v && setSelectedGroupId(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 40 }}
              >
                {groups.map((g) => (
                  <Tab
                    key={g.id}
                    value={g.id}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <span>{g.name}</span>
                        {typeof g.studentCount === 'number' && (
                          <Chip label={g.studentCount} size="small" sx={{ height: 20 }} />
                        )}
                      </Box>
                    }
                    sx={{ minHeight: 40 }}
                  />
                ))}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={async () => {
                  const name = window.prompt('Nom du nouveau groupe');
                  if (!name?.trim()) return;
                  try {
                    const token = localStorage.getItem('admin_token');
                    const response = await fetch(
                      `${process.env.REACT_APP_API_URL}/groups/course/${courseId}`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ name })
                      }
                    );
                    if (response.ok) {
                      await fetchGroups(courseId);
                    } else {
                      const err = await response.json();
                      setError(err.error || 'Erreur lors de la création du groupe');
                    }
                  } catch (err) {
                    console.error('Error creating group:', err);
                    setError('Erreur lors de la création du groupe');
                  }
                }}
              >
                Ajouter un groupe
              </Button>
              {selectedGroupId && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    const g = groups.find(x => x.id === selectedGroupId);
                    if (g && window.confirm(`Supprimer le groupe « ${g.name } » ? (impossible s’il a des inscrits actifs)`)) {
                      deleteGroupById(selectedGroupId);
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {mainTab === 'groups' && isEditing && courseId && groups.length > 0 && (
        <Card
          sx={{
            mb: 0,
            mt: 0,
            borderRadius: 0,
            borderTop: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Parametres pedagogiques du groupe : {groups.find((g) => g.id === selectedGroupId)?.name || 'Aucun'}
            </Typography>
            {selectedGroupId && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Formation
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {user?.role === 'admin' && (
                      <FormControl size="small" sx={{ minWidth: 320 }}>
                        <InputLabel>Formateur (coach) du groupe</InputLabel>
                        <Select
                          label="Formateur (coach) du groupe"
                          value={groups.find(g => g.id === selectedGroupId)?.trainer ?? ''}
                          onChange={(e) => updateGroupTrainer(selectedGroupId, e.target.value || null)}
                        >
                          <MenuItem value="">
                            <em>Aucun</em>
                          </MenuItem>
                          {coaches.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <TextField
                      type="datetime-local"
                      size="small"
                      label="Date et heure de deblocage formation"
                      InputLabelProps={{ shrink: true }}
                      value={toDateTimeLocal(
                        groups.find((g) => g.id === selectedGroupId)?.courseUnlockDate ?? undefined
                      )}
                      onChange={(e) => {
                        const v = e.target.value;
                        void updateGroupCourseUnlock(selectedGroupId, v ? fromDateTimeLocal(v) : null);
                      }}
                      sx={{ minWidth: 320 }}
                    />

                    {course.courseType === 'blended' && (
                      <>
                        <TextField
                          type="date"
                          size="small"
                          label="Certificat — début de formation"
                          InputLabelProps={{ shrink: true }}
                          value={(
                            groups.find((g) => g.id === selectedGroupId)?.certificateStartDate ?? ''
                          ).slice(0, 10)}
                          onChange={(e) =>
                            void updateGroupCertificateDates(
                              selectedGroupId,
                              'certificateStartDate',
                              e.target.value || null
                            )
                          }
                          sx={{ minWidth: 240 }}
                        />
                        <TextField
                          type="date"
                          size="small"
                          label="Certificat — fin de formation"
                          InputLabelProps={{ shrink: true }}
                          helperText="Le certificat devient téléchargeable une fois cette date passée"
                          value={(
                            groups.find((g) => g.id === selectedGroupId)?.certificateEndDate ?? ''
                          ).slice(0, 10)}
                          onChange={(e) =>
                            void updateGroupCertificateDates(
                              selectedGroupId,
                              'certificateEndDate',
                              e.target.value || null
                            )
                          }
                          sx={{ minWidth: 240 }}
                        />
                      </>
                    )}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <MarkdownEditorMultilingual
                      label="Mot de bienvenue (RichText / HTML)"
                      value={groups.find((g) => g.id === selectedGroupId)?.welcomeMessage || {}}
                      onChange={(value) => {
                        handleGroupWelcomeMessageChange(selectedGroupId, value);
                      }}
                      availableLanguages={course.availableLanguages}
                      placeholder="Message affiché sur la page résumé de la formation pour ce groupe..."
                      height={220}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Affiché entre la barre de progression et le parcours. L&apos;apprenant peut le fermer définitivement.
                      Enregistré avec le bouton « Sauvegarder » en bas de page.
                    </Typography>
                  </Box>
                </Box>

                {course.chapters.map((chapter, chapterIndex) => {
                  const chapterId = chapter.id || chapter._id || '';
                  const chapterOverride = getChapterOverride(selectedGroupId, chapterId);
                  const workshops = (chapter.items || []).filter((item) => item.itemType === 'workshop') as Array<IWorkshop & { itemType: 'workshop' }>;

                  return (
                    <Accordion
                      key={`group-chapter-${chapter.id || chapterIndex}`}
                      sx={{
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div">
                        {(() => {
                          const chapterTitle =
                            chapter.title[course.defaultLanguage] ||
                            chapter.title.fr ||
                            chapter.title.en ||
                            Object.values(chapter.title || {})[0] ||
                            'Sans titre';
                          const chapterLabel =
                            chapter.description?.[course.defaultLanguage] ||
                            chapter.description?.fr ||
                            chapter.description?.en ||
                            Object.values(chapter.description || {})[0] ||
                            '';
                          const headerText = chapterLabel ? `${chapterLabel} : ${chapterTitle}` : chapterTitle;
                          return (
                            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                              {headerText}
                            </Typography>
                          );
                        })()}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box display="flex" gap={2} mt={1} mb={2} flexWrap="wrap">
                          <TextField
                            label="Date et heure de déblocage"
                            type="datetime-local"
                            value={toDateTimeLocal(chapterOverride.unlockDate)}
                            onChange={(e) => {
                              const value = e.target.value;
                              void updateGroupChapterOverrides(selectedGroupId, chapterId, {
                                unlockDate: value ? fromDateTimeLocal(value) : undefined,
                                deadline: chapterOverride.deadline
                              });
                            }}
                            size="small"
                            sx={{ minWidth: 280 }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Date limite"
                            type="datetime-local"
                            value={toDateTimeLocal(chapterOverride.deadline)}
                            onChange={(e) => {
                              const value = e.target.value;
                              void updateGroupChapterOverrides(selectedGroupId, chapterId, {
                                deadline: value ? fromDateTimeLocal(value) : undefined,
                                unlockDate: chapterOverride.unlockDate
                              });
                            }}
                            size="small"
                            sx={{ minWidth: 280 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>

                        {workshops.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Aucun workshop dans cette section.
                          </Typography>
                        ) : (
                          workshops.map((workshop, workshopIndex) => {
                            const workshopId = (workshop as any)._id || (workshop as any).id;
                            const workshopData =
                              (workshopId ? getWorkshopOverride(selectedGroupId, chapterId, workshopId, workshop) : null) || workshop;

                            const workshopTitleText =
                              workshopData.title?.[course.defaultLanguage] ||
                              workshopData.title?.fr ||
                              workshopData.title?.en ||
                              Object.values(workshopData.title || {})[0] ||
                              'Sans titre';

                            const workshopLabelText =
                              workshopData.description?.[course.defaultLanguage] ||
                              workshopData.description?.fr ||
                              workshopData.description?.en ||
                              Object.values(workshopData.description || {})[0] ||
                              '';

                            const workshopCardTitle = workshopLabelText.trim()
                              ? `${workshopLabelText.trim()} : ${workshopTitleText}`
                              : workshopTitleText;

                            return (
                              <Card key={`group-workshop-${chapterId}-${workshopId || workshopIndex}`} variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5 }}>
                                    {workshopCardTitle}
                                  </Typography>

                                  <Box display="flex" gap={2} flexWrap="wrap">
                                    <TextField
                                      type="date"
                                      label="Date de l'atelier"
                                      value={workshopData.date || ''}
                                      onChange={(e) => {
                                        void updateWorkshopOverrideField(selectedGroupId, chapterId, workshop, { date: e.target.value });
                                      }}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                      sx={{ minWidth: 200 }}
                                    />
                                    <TextField
                                      type="time"
                                      label="Heure de début"
                                      value={workshopData.startTime || ''}
                                      onChange={(e) => {
                                        void updateWorkshopOverrideField(selectedGroupId, chapterId, workshop, { startTime: e.target.value });
                                      }}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                      type="time"
                                      label="Heure de fin"
                                      value={workshopData.endTime || ''}
                                      onChange={(e) => {
                                        void updateWorkshopOverrideField(selectedGroupId, chapterId, workshop, { endTime: e.target.value });
                                      }}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Box>

                                  <MultilingualInput
                                    label="Lieu"
                                    value={workshopData.lieu || {}}
                                    onChange={(value) => {
                                      void updateWorkshopOverrideField(selectedGroupId, chapterId, workshop, { lieu: value });
                                    }}
                                    availableLanguages={course.availableLanguages}
                                    multiline
                                    rows={2}
                                  />

                                  <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={(
                                      <Checkbox
                                        checked={Boolean(workshopData.distanciel)}
                                        onChange={(e) => {
                                          void updateWorkshopOverrideField(selectedGroupId, chapterId, workshop, { distanciel: e.target.checked });
                                        }}
                                      />
                                    )}
                                    label="Distanciel"
                                  />
                                </CardContent>
                              </Card>
                            );
                          })
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {/* Mesures d'impact de suivi : en bas de l'onglet, après les sections (chapitres) */}
                {assessmentEnabled && (assessmentDoc.followupImpacts || []).length > 0 && (
                  <Box
                    sx={{
                      mt: 5,
                      pt: 4,
                      px: { xs: 0, sm: 1 },
                      borderTop: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Mesures d'impact de suivi
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720, fontSize: '0.8125rem' }}>
                      Définissez la date de déblocage de chaque mesure de suivi pour ce groupe.
                      Sans date, la mesure ne sera jamais disponible pour les apprenants du groupe.
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                      }}
                    >
                      {(assessmentDoc.followupImpacts || []).map((followup, fuIndex) => {
                        const fid = followup._id ? String(followup._id) : '';
                        const groupSel = groups.find((g) => g.id === selectedGroupId);
                        const existing = groupSel?.followupImpactDates?.find(
                          (d) => String(d.followupId) === fid
                        );
                        const labelText = pickLocalizedTranslation(followup.label, {
                          preferredLang: course.defaultLanguage || 'en',
                          fallback: `Mesure de suivi ${fuIndex + 1}`,
                        });
                        return (
                          <Box
                            key={fid || 'new'}
                            display="flex"
                            alignItems="center"
                            gap={2}
                            flexWrap="wrap"
                          >
                            <Typography variant="body1" fontWeight={600} sx={{ minWidth: 240 }}>
                              {labelText}
                            </Typography>
                            <TextField
                              type="datetime-local"
                              size="small"
                              label="Date de déblocage"
                              InputLabelProps={{ shrink: true }}
                              value={toDateTimeLocal(existing?.unlockDate)}
                              onChange={(e) => {
                                const v = e.target.value;
                                void updateGroupFollowupDate(
                                  selectedGroupId,
                                  fid,
                                  v ? fromDateTimeLocal(v) : null
                                );
                              }}
                              sx={{ minWidth: 280 }}
                            />
                            {!existing && (
                              <Typography variant="caption" color="text.disabled">
                                Non programmée pour ce groupe
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diagnostic & Impact */}
      {mainTab === 'assessment' && isEditing && courseId && (
        <Card
          sx={{
            mb: 3,
            mt: 0,
            borderRadius: '0 0 8px 8px',
            borderTop: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <CardContent>
            {assessmentLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <AssessmentEditor
                  enabled={assessmentEnabled}
                  onToggleEnabled={handleToggleAssessmentEnabled}
                  assessment={assessmentDoc}
                  onChange={setAssessmentDoc}
                  availableLanguages={course.availableLanguages || ['en']}
                  defaultLanguage={course.defaultLanguage || 'en'}
                  followupIdsWithResponses={followupIdsWithResponses}
                />
                {/* La sauvegarde passe par la barre globale "Modifications en attente" en bas. */}
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {assessmentDirty
                      ? 'Modifications non sauvegardées — utilisez le bouton « Sauvegarder » en bas de page.'
                      : 'Toutes les modifications sont sauvegardées.'}
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}
      {mainTab === 'assessment' && !isEditing && (
        <Card
          sx={{
            mb: 3,
            mt: 0,
            borderRadius: '0 0 8px 8px',
            borderTop: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <CardContent>
            <Alert severity="info">
              Cette section sera disponible une fois la formation enregistrée.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Course Information */}
      {mainTab === 'general' && (
      <Accordion
        sx={{
          mb: 3,
          border: 'none',
          borderRadius: '0 0 8px 8px !important',
          borderTop: 'none',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <AccordionDetails>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}>Langues disponibles</Typography>
          <FormGroup row sx={{ mb: 2 }}>
            {AVAILABLE_LANGUAGES.map(lang => (
              <FormControlLabel
                key={lang.code}
                control={
                  <Checkbox
                    checked={course.availableLanguages.includes(lang.code)}
                    onChange={() => handleLanguageToggle(lang.code)}
                    size="small"
                  />
                }
                label={lang.name}
              />
            ))}
          </FormGroup>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Langue par défaut</InputLabel>
            <Select
              value={course.defaultLanguage}
              label="Langue par défaut"
              onChange={(e) => setCourse(prev => ({ ...prev, defaultLanguage: e.target.value }))}
            >
              {course.availableLanguages.map(code => (
                <MenuItem key={code} value={code}>
                  {AVAILABLE_LANGUAGES.find(l => l.code === code)?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MultilingualInput
            label="Titre de la formation (apprenant)"
            value={course.title}
            onChange={(value) => setCourse(prev => ({ ...prev, title: value }))}
            availableLanguages={course.availableLanguages}
            required
          />
          <TextField
            label="Nom de projet formation (admin)"
            value={course.courseProject}
            onChange={(e) => setCourse(prev => ({ ...prev, courseProject: e.target.value }))}
            fullWidth
            margin="normal"
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}>
            Tags
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Sélectionnez une ou plusieurs étiquettes pour classifier la formation.
          </Typography>
          <FormGroup row sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {COURSE_TAG_OPTIONS.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    checked={(course.tags || []).includes(tag)}
                    onChange={() =>
                      setCourse((prev) => {
                        const cur = prev.tags || [];
                        const next = cur.includes(tag)
                          ? cur.filter((t) => t !== tag)
                          : [...cur, tag];
                        return { ...prev, tags: next };
                      })
                    }
                    size="small"
                  />
                }
                label={tag}
              />
            ))}
          </FormGroup>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Type de formation</InputLabel>
            <Select
              value={course.courseType ?? ''}
              label="Type de formation"
              onChange={(e) =>
                setCourse((prev) => ({
                  ...prev,
                  courseType: (e.target.value || null) as Course['courseType'],
                }))
              }
            >
              <MenuItem value="">
                <em>Non défini</em>
              </MenuItem>
              {COURSE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Détermine le certificat de réalisation : e-learning (dates auto), blended (dates par
              groupe), coaching (pas de certificat).
            </Typography>
          </FormControl>

          {isEditing && (
            <>
              <MultilingualInput
                label="Description de la formation"
                value={course.description}
                onChange={(value) => setCourse(prev => ({ ...prev, description: value }))}
                availableLanguages={course.availableLanguages}
                multiline
                rows={4}
                required
              />

              <Box sx={{ mt: 3 }}>
                <ImageUpload
                  label="Image du cours"
                  currentImage={course.image}
                  onImageUploaded={(imageUrl) => setCourse(prev => ({ ...prev, image: imageUrl }))}
                  onImageRemoved={() => setCourse(prev => ({ ...prev, image: undefined }))}
                  forcedAspectRatio={3 / 4}
                  hideAspectRatioSelector
                />
              </Box>
            </>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            Les champs liés aux groupes (formateur de groupe, date de déblocage formation, dates par groupe)
            se configurent dans l&apos;onglet <strong>Groupes</strong>.
          </Alert>
        </AccordionDetails>
      </Accordion>
      )}

      {/* Chapters */}
      {mainTab === 'chapters' && (
      <Card
        sx={{
          mb: 3,
          mt: 0,
          borderRadius: '0 0 8px 8px',
          borderTop: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="600">Sections de la formation</Typography>
            {isEditing ? (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addChapter}>
                Ajouter une section
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Sections disponibles en édition
              </Typography>
            )}
          </Box>

          {isEditing && course.chapters.map((chapter, chapterIndex) => (
            <Accordion 
              key={(chapter as any)._id || chapter.id || `chapter-${chapter.order}-${chapterIndex}`}
              sx={{ 
                mb: 2,
                border: '2px solid #e0e0e0',
                borderRadius: '8px !important',
                '&:before': {
                  display: 'none',
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div">
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
                    {(() => {
                      const chapterTitle =
                        chapter.title[course.defaultLanguage] ||
                        chapter.title.fr ||
                        chapter.title.en ||
                        Object.values(chapter.title || {})[0] ||
                        'Sans titre';
                      const chapterLabel =
                        chapter.description?.[course.defaultLanguage] ||
                        chapter.description?.fr ||
                        chapter.description?.en ||
                        Object.values(chapter.description || {})[0] ||
                        '';
                      const headerText = chapterLabel ? `${chapterLabel} : ${chapterTitle}` : chapterTitle;
                      return (
                    <Typography fontWeight="500">
                          {headerText}
                    </Typography>
                      );
                    })()}
                    <Chip label={`${chapter.items.length} items`} size="small" />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Monter la section">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveChapter(chapterIndex, 'up');
                          }}
                          disabled={chapterIndex === 0}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Descendre la section">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveChapter(chapterIndex, 'down');
                          }}
                          disabled={chapterIndex === course.chapters.length - 1}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Supprimer la section">
                      <IconButton 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (window.confirm(`Êtes-vous sûr de vouloir supprimer la section « ${chapter.title[course.defaultLanguage] || 'Sans titre'} » ? Cette action supprimera également tous les éléments qu'elle contient.`)) {
                            deleteChapter(chapterIndex);
                          }
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Tabs
                  value={chapterTabByIndex[chapterIndex] ?? 0}
                  onChange={(_, v) => setChapterTabByIndex((prev) => ({ ...prev, [chapterIndex]: v }))}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Informations" value={0} />
                  <Tab label="Modules" value={1} />
                </Tabs>

                {(chapterTabByIndex[chapterIndex] ?? 0) === 0 && (
                  <Box>
                    <MultilingualInput
                      label="Titre de la section"
                      value={chapter.title}
                      onChange={(value) => updateChapter(chapterIndex, { title: value })}
                      availableLanguages={course.availableLanguages}
                      required
                    />

                    <MultilingualInput
                      label="Label"
                      value={chapter.description || {}}
                      onChange={(value) => updateChapter(chapterIndex, { description: value })}
                      availableLanguages={course.availableLanguages}
                    />

                    <MarkdownEditorMultilingual
                      label="Résumé de la section (RichText / HTML)"
                      value={chapter.summary || {}}
                      onChange={(value) => updateChapter(chapterIndex, { summary: value })}
                      availableLanguages={course.availableLanguages}
                      placeholder="Saisissez le résumé de la section (HTML)..."
                      height={300}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}
                      >
                        Icône de la section
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            sm: 'repeat(4, minmax(0, 1fr))',
                          },
                          gap: 1.5,
                        }}
                      >
                        {CHAPTER_ICON_OPTIONS.map((option) => {
                          const isSelected = (chapter.icon || '') === option.value;
                          return (
                            <Card
                              key={option.value || 'default'}
                              variant="outlined"
                              onClick={() => updateChapter(chapterIndex, { icon: option.value || undefined })}
                              sx={{
                                cursor: 'pointer',
                                borderWidth: isSelected ? 2 : 1,
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  transform: 'translateY(-1px)',
                                },
                              }}
                            >
                              <CardContent
                                sx={{
                                  py: 1.5,
                                  px: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  '&:last-child': { pb: 1.5 },
                                }}
                              >
                                <Box sx={{ color: isSelected ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                                  {option.icon}
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Box>

                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      Les dates de section par groupe (unlock/deadline) sont configurables dans l&apos;onglet
                      <strong> Groupes</strong>.
                    </Alert>

                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={(
                        <Switch
                          checked={chapter.requiresRating !== false}
                          onChange={(e) => updateChapter(chapterIndex, { requiresRating: e.target.checked })}
                        />
                      )}
                      label="Demander une note en fin de section"
                    />
                  </Box>
                )}

                {(chapterTabByIndex[chapterIndex] ?? 0) === 1 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={2}>
                      <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}>Modules de la section</Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          startIcon={<CapsuleIcon />}
                          variant="outlined"
                          onClick={() => openAddItemDialog(chapterIndex, 'capsule')}
                        >
                          Capsule
                        </Button>
                        <Button
                          size="small"
                          startIcon={<QuizIcon />}
                          variant="outlined"
                          onClick={() => openAddItemDialog(chapterIndex, 'quiz')}
                        >
                          Quiz
                        </Button>
                        <Button
                          size="small"
                          startIcon={<WorkshopIcon />}
                          variant="outlined"
                          onClick={() => openAddItemDialog(chapterIndex, 'workshop')}
                        >
                          Atelier
                        </Button>
                      </Box>
                    </Box>

                    {chapter.items.map((item, itemIndex) => (
                      <Card
                        key={(item as any)._id || (item as any).id || `item-${chapter.order}-${item.order}-${itemIndex}`}
                        variant="outlined"
                        sx={{ mb: 1 }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            {getItemIcon(item.itemType)}
                            <Box flexGrow={1}>
                              {(() => {
                                const itemTitle =
                                  item.title?.[course.defaultLanguage] ||
                                  item.title?.fr ||
                                  item.title?.en ||
                                  Object.values(item.title || {})[0] ||
                                  'Sans titre';
                                const itemLabel =
                                  item.description?.[course.defaultLanguage] ||
                                  item.description?.fr ||
                                  item.description?.en ||
                                  Object.values(item.description || {})[0] ||
                                  '';
                                const itemHeaderText = itemLabel ? `${itemLabel} : ${itemTitle}` : itemTitle;
                                return (
                                  <Typography variant="body2" fontWeight="500">
                                    {itemHeaderText}
                                  </Typography>
                                );
                              })()}
                              <Box display="flex" gap={1} mt={0.5}>
                                <Chip label={item.itemType} size="small" variant="outlined" />
                                <Chip label={`Ordre : ${item.order}`} size="small" />
                                {item.itemType === 'capsule' && (
                                  <Chip 
                                    label={`${item.modules.length} modules`} 
                                    size="small"
                                    title={`Capsule ID: ${item.order}, Modules: ${JSON.stringify(item.modules.map(m => m.type))}`}
                                  />
                                )}
                                {item.itemType === 'quiz' && (
                                  <Chip label={`${item.questions.length} questions`} size="small" />
                                )}
                              </Box>
                            </Box>
                            <Box display="flex" gap={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => moveItem(chapterIndex, itemIndex, 'up')}
                                disabled={itemIndex === 0}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveItem(chapterIndex, itemIndex, 'down')}
                                disabled={itemIndex === chapter.items.length - 1}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => openEditItemDialog(chapterIndex, itemIndex)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const itemName = item.title?.[course.defaultLanguage] || item.title?.fr || item.title?.en || Object.values(item.title || {})[0] || 'Untitled';
                                  if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${itemName}" (${item.itemType}) ? Cette action est irréversible.`)) {
                                    deleteItem(chapterIndex, itemIndex);
                                  }
                                }}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
      )}

      {/* Item Edit/Add Dialog */}
      <Dialog 
        open={itemDialogOpen} 
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          setItemDialogOpen(false);
        }}
        maxWidth="md" 
        fullWidth
        disableEnforceFocus
        PaperProps={{ sx: { minHeight: '500px' } }}
      >
        <DialogTitle>
          {editingItem.item?.itemType === 'workshop'
            ? "Edition de l'atelier"
            : `${editingItem.itemIndex === null ? 'Ajouter' : 'Modifier'} ${editingItem.item?.itemType}`
          }
        </DialogTitle>
        <DialogContent>
          {/* Les capsules sont maintenant gérées directement par CapsuleEditorDialog */}

          {editingItem.item?.itemType === 'quiz' && (
            <QuizEditor
              quiz={editingItem.item}
              onChange={updateItemInDialog}
              availableLanguages={course.availableLanguages}
              isOpen={itemDialogOpen}
            />
          )}

          {editingItem.item?.itemType === 'workshop' && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Dans cette popup, vous modifiez uniquement les champs communs a tous les groupes
                (titre, label, image, description). Les champs par groupe se configurent dans l&apos;onglet <strong>Groupes</strong>.
              </Alert>

              <Box>
                <MultilingualInput
                  label="Titre de l'atelier"
                  value={(editingItem.item as IWorkshop).title || {}}
                  onChange={(value) => {
                    updateItemInDialog({
                      ...(editingItem.item as IWorkshop),
                      title: value
                    });
                  }}
                  availableLanguages={course.availableLanguages}
                  required
                />

                <MultilingualInput
                  label="Label"
                  value={(editingItem.item as IWorkshop).description || {}}
                  onChange={(value) => {
                    updateItemInDialog({
                      ...(editingItem.item as IWorkshop),
                      description: value
                    });
                  }}
                  availableLanguages={course.availableLanguages}
                  required
                />

                <Box sx={{ mt: 3, mb: 3 }}>
                  <ImageUpload
                    label="Image du workshop"
                    currentImage={(editingItem.item as IWorkshop).image}
                    onImageUploaded={(imageUrl) => {
                      updateItemInDialog({
                        ...(editingItem.item as IWorkshop),
                        image: imageUrl
                      });
                    }}
                    onImageRemoved={() => {
                      updateItemInDialog({
                        ...(editingItem.item as IWorkshop),
                        image: undefined
                      });
                    }}
                    forcedAspectRatio={9 / 3}
                    hideAspectRatioSelector
                  />
                </Box>

                <MarkdownEditorMultilingual
                  label="Détails de l'atelier"
                  value={(editingItem.item as IWorkshop).details || {}}
                  onChange={(value) => {
                    updateItemInDialog({
                      ...(editingItem.item as IWorkshop),
                      details: value
                    });
                  }}
                  availableLanguages={course.availableLanguages}
                  placeholder="Saisissez la description du workshop (HTML)..."
                  height={300}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialogOpen(false)}>Annuler</Button>
          <Button onClick={saveItemFromDialog} variant="contained">
            {editingItem.itemIndex === null ? 'Ajouter' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de vérification de complétude */}
      <Dialog
        open={validationOpen}
        onClose={() => setValidationOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ValidateIcon color="primary" />
            <span>Vérification de la complétude</span>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {validationResult && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Langues vérifiées :{' '}
                {validationResult.checkedLanguages.map((c) => languageName(c)).join(', ')}
                {' '}— langue par défaut : <strong>{languageName(validationResult.defaultLanguage)}</strong>.
              </Typography>

              {validationResult.issues.length === 0 ? (
                <Alert severity="success" icon={<SavedIcon />}>
                  Tous les champs obligatoires sont renseignés et toutes les traductions sont complètes.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {validationResult.issues.length} champ(s) à compléter.
                  </Alert>

                  {Array.from(
                    validationResult.issues.reduce((map, issue) => {
                      const list = map.get(issue.path) || [];
                      list.push(issue);
                      map.set(issue.path, list);
                      return map;
                    }, new Map<string, typeof validationResult.issues>())
                  ).map(([path, issues], groupIdx) => (
                    <Box key={path} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {path}
                      </Typography>
                      {issues.map((issue, idx) => (
                        <Box
                          key={`${issue.field}-${idx}`}
                          display="flex"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1}
                          sx={{ pl: 1.5, py: 0.4 }}
                        >
                          {issue.kind === 'required' && (
                            <RequiredIcon sx={{ fontSize: 18, color: 'error.main' }} />
                          )}
                          {issue.kind === 'translation' && (
                            <TranslateIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                          )}
                          {issue.kind === 'structural' && (
                            <RequiredIcon sx={{ fontSize: 18, color: 'error.main' }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {issue.field}
                          </Typography>
                          {issue.kind === 'structural' ? (
                            <Typography variant="body2" color="text.secondary">
                              {issue.detail}
                            </Typography>
                          ) : (
                            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                              <Typography variant="caption" color="text.secondary">
                                {issue.kind === 'required'
                                  ? 'manquant en'
                                  : 'traduction manquante en'}
                              </Typography>
                              {(issue.missingLanguages || []).map((lang) => (
                                <Chip
                                  key={lang}
                                  label={languageName(lang)}
                                  size="small"
                                  color={issue.kind === 'required' ? 'error' : 'warning'}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                      {groupIdx < validationResult.issues.length && <Divider sx={{ mt: 1 }} />}
                    </Box>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={runCompletenessCheck}>Revérifier</Button>
          <Button onClick={() => setValidationOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour l'édition des capsules */}
      <CapsuleEditorDialog
        open={capsuleDialogOpen}
        capsule={editingCapsule}
        onSave={async (updatedCapsule) => {
          console.log('💾 Sauvegarde de la capsule:', updatedCapsule);
          console.log('🖼️ Image de la capsule:', updatedCapsule.image);
          console.log('📍 editingItem:', editingItem);
          
          if (editingItem.chapterIndex >= 0) {
            try {
              const updatedChapters = [...course.chapters];
              const chapter = updatedChapters[editingItem.chapterIndex];
              
              // Preserve _id from original item if editing existing capsule
              const originalItem = editingItem.itemIndex !== null 
                ? chapter.items[editingItem.itemIndex] 
                : null;
              const originalId = originalItem && '_id' in originalItem 
                ? (originalItem as any)._id 
                : undefined;
              
              // Map estimatedDuration to duration for backend compatibility
              const itemWithType = { 
                ...updatedCapsule, 
                itemType: 'capsule' as const,
                duration: updatedCapsule.estimatedDuration, // Map estimatedDuration to duration
                ...(originalId && { _id: originalId }) // Preserve _id if editing
              };
              console.log('📦 Item avec type:', itemWithType);
              console.log('🖼️ Image dans itemWithType:', itemWithType.image);
              if (originalId) {
                console.log('🔑 ID préservé:', originalId);
              }
              
              if (editingItem.itemIndex !== null) {
                // Édition d'une capsule existante
                console.log('✏️ Édition de la capsule à l\'index:', editingItem.itemIndex);
                chapter.items[editingItem.itemIndex] = itemWithType as ChapterItem;
              } else {
                // Création d'une nouvelle capsule
                console.log('➕ Création d\'une nouvelle capsule');
                chapter.items.push(itemWithType as ChapterItem);
              }
              
              // Mettre à jour l'état local
              const updatedCourse = { ...course, chapters: updatedChapters };
              console.log('🔄 Mise à jour de l\'état local avec:', chapter.items);
              setCourse(updatedCourse);
              
              // Sauvegarder immédiatement les chapitres
              console.log('🔄 Sauvegarde des chapitres avec la capsule...');
              await saveChapters(updatedChapters, true, true); // silent save
              
              console.log('✅ Capsule sauvegardée avec succès');
              
              // Mettre à jour editingItem pour refléter les changements
              setEditingItem(prev => ({
                ...prev,
                item: itemWithType
              }));
            } catch (error) {
              console.error('❌ Erreur lors de la sauvegarde de la capsule:', error);
              setError('Erreur lors de la sauvegarde de la capsule');
            }
          }
          
          setCapsuleDialogOpen(false);
          setEditingCapsule(null);
        }}
        onCancel={() => {
          setCapsuleDialogOpen(false);
          setEditingCapsule(null);
        }}
        availableLanguages={course.availableLanguages}
      />
    </Container>
  );
};

export default CourseBuilderV2;

